import { jsPDF } from "jspdf";
import logoHeader from "../assets/img/LogoHeaderInfra.png";
import footerBanner from "../assets/img/InfraFooter.png";
import { ExpedienteGridRow } from "../dtos/CatCabeceraManifold";
import { ManifoldEnvaseSerie } from "./manifoldEnvases";

export interface ExpedientePdfData {
  row: ExpedienteGridRow;
  series: ManifoldEnvaseSerie[];
  horaInicio: string;
  horaTermino: string;
  /** Nombre completo del supervisor (firma). */
  supervisorNombreCompleto?: string;
}

const COLORS = {
  text: "#1A1A1A",
  navy: "#003366",
  orange: "#E36C0A",
  label: "#8A8A8A",
  zebra: "#F3F5F7",
  line: "#D0D5DD",
  muted: "#666666",
};

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const pad = (n: number | string, size = 2) =>
  String(n).replace(/\D/g, "").padStart(size, "0") || "".padStart(size, "0");

const formatFechaLlenado = (value?: string | null) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value.slice(0, 10);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * Folio PDF:
 * [Sucursal]-[Manifold]-[Línea][Parada][Turno]-[AAMMDD]
 * Ejemplo: 184-05-232-260720
 */
const buildFolio = (row: ExpedienteGridRow) => {
  const fecha = row.fechaLlenado ? new Date(row.fechaLlenado) : new Date();
  const aammdd = Number.isNaN(fecha.getTime())
    ? "000000"
    : `${String(fecha.getFullYear()).slice(-2)}${pad(fecha.getMonth() + 1)}${pad(
        fecha.getDate(),
      )}`;

  const sucursal = String(row.numSucursal || row.id_Sucursal || "0");

  const manifoldDigits =
    String(row.manifoldNombre ?? "").match(/\d+/)?.[0] ??
    String(row.id_Manifold || 0);
  const manifold = pad(manifoldDigits, 2);

  const linea = String(row.linea || 0);
  const parada = String(row.parada || 0);
  const turnoDigits =
    String(row.turno ?? "").match(/\d+/)?.[0] ?? String(row.turno || "0");
  const lineaParadaTurno = `${linea}${parada}${turnoDigits}`;

  return `${sucursal}-${manifold}-${lineaParadaTurno}-${aammdd}`;
};

const drawMetaField = (
  doc: jsPDF,
  x: number,
  y: number,
  label: string,
  value: string,
  options?: { valueColor?: string; maxWidth?: number },
) => {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(COLORS.label);
  doc.text(label.toUpperCase(), x, y);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  doc.setTextColor(options?.valueColor ?? COLORS.text);
  const lines = doc.splitTextToSize(String(value || "—"), options?.maxWidth ?? 85);
  doc.text(lines, x, y + 4.2);
  return 4.2 + lines.length * 4;
};

export const generateExpedienteProduccionPdf = async (
  data: ExpedientePdfData,
): Promise<void> => {
  const { row, series, horaInicio, horaTermino, supervisorNombreCompleto } =
    data;
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "letter" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const marginX = 16;
  const contentW = pageW - marginX * 2;

  const [logo, footer] = await Promise.all([
    loadImage(logoHeader),
    loadImage(footerBanner),
  ]);

  // Header: logo | título | folio
  const logoH = 12;
  const logoW = (logo.width / logo.height) * logoH;
  doc.addImage(logo, "PNG", marginX, 11, logoW, logoH);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(COLORS.text);
  doc.text("EXPEDIENTE DE PRODUCCIÓN", pageW / 2, 18.5, { align: "center" });

  const folio = buildFolio(row);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(COLORS.orange);
  doc.text(folio, pageW - marginX, 18.5, { align: "right" });

  // Metadata (sin cajas): etiqueta gris + valor en negrita
  const leftX = marginX;
  const rightX = marginX + contentW / 2 + 4;
  const colMaxW = contentW / 2 - 8;
  let yLeft = 30;
  let yRight = 30;

  const supervisorDisplay =
    supervisorNombreCompleto?.trim() ||
    (row.supervisorNombre !== "—" ? row.supervisorNombre : "—");

  const leftFields: Array<[string, string, string?]> = [
    ["Sucursal", row.sucursalLabel],
    ["Producto", row.productoNombre],
    ["Parada", String(row.parada || "—"), COLORS.orange],
    ["Operador", row.operadorNombre || "—"],
    ["Fecha de llenado", formatFechaLlenado(row.fechaLlenado)],
    ["Hora inicio", horaInicio || "—"],
  ];

  const rightFields: Array<[string, string]> = [
    ["Manifold", row.manifoldNombre || "—"],
    ["Línea", String(row.linea || "—")],
    ["Turno", String(row.turno || "—")],
    ["Supervisor", supervisorDisplay],
    [
      "Capacidad",
      row.capacidadTotal_M3 != null
        ? `${Number(row.capacidadTotal_M3).toFixed(3)} m3`
        : "—",
    ],
    ["Hora de término", horaTermino || "—"],
  ];

  for (const [label, value, color] of leftFields) {
    yLeft +=
      drawMetaField(doc, leftX, yLeft, label, value, {
        valueColor: color,
        maxWidth: colMaxW,
      }) + 3.5;
  }
  for (const [label, value] of rightFields) {
    yRight +=
      drawMetaField(doc, rightX, yRight, label, value, {
        maxWidth: colMaxW,
      }) + 3.5;
  }

  let y = Math.max(yLeft, yRight) + 4;

  // Título sección cilindros
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(COLORS.navy);
  doc.text("REGISTRO DE CILINDROS", pageW / 2, y, { align: "center" });
  y += 5;

  const slots = Math.max(48, Math.ceil(series.length / 3) * 3 || 48);
  const perCol = Math.ceil(slots / 3);
  const segGap = 3;
  const segW = (contentW - segGap * 2) / 3;
  const noW = 11;
  const serieW = segW - noW;
  const cellH = 5;

  const drawSegHeader = (sx: number) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(COLORS.navy);
    doc.text("NO.", sx + noW / 2, y + 3.2, { align: "center" });
    doc.text("NO. SERIE", sx + noW + 1.5, y + 3.2);
    doc.setDrawColor(COLORS.navy);
    doc.setLineWidth(0.35);
    doc.line(sx, y + cellH - 0.4, sx + segW, y + cellH - 0.4);
  };

  drawSegHeader(marginX);
  drawSegHeader(marginX + segW + segGap);
  drawSegHeader(marginX + (segW + segGap) * 2);
  y += cellH;

  const startY = y;
  for (let col = 0; col < 3; col++) {
    let cy = startY;
    const sx = marginX + col * (segW + segGap);
    for (let i = 0; i < perCol; i++) {
      const idx = col * perCol + i;
      const n = idx + 1;
      const serie = series[idx]?.numeroSerie ?? "";

      if (i % 2 === 1) {
        doc.setFillColor(COLORS.zebra);
        doc.rect(sx, cy, segW, cellH, "F");
      }

      doc.setDrawColor(COLORS.line);
      doc.setLineWidth(0.15);
      doc.line(sx, cy + cellH, sx + segW, cy + cellH);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(COLORS.text);
      doc.text(String(n), sx + noW / 2, cy + 3.4, { align: "center" });
      if (serie) {
        doc.text(serie, sx + noW + 1.5, cy + 3.4, {
          maxWidth: serieW - 2.5,
        });
      }
      cy += cellH;
    }
  }
  y = startY + perCol * cellH + 12;

  // Firma
  const firmaNombre =
    supervisorNombreCompleto?.trim() ||
    (row.supervisorNombreCompleto && row.supervisorNombreCompleto !== "—"
      ? row.supervisorNombreCompleto
      : row.supervisorNombre !== "—"
        ? row.supervisorNombre
        : "Nombre de Usuario");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(COLORS.text);
  doc.text(firmaNombre, pageW / 2, y, { align: "center" });
  y += 4.5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(COLORS.muted);
  doc.text("Supervisor de Producción", pageW / 2, y, { align: "center" });

  y += 8;
  doc.setFontSize(6.5);
  doc.setTextColor(COLORS.muted);
  const disclaimer =
    "Este expediente se genera de forma automática a partir de los registros capturados en la App Móvil Etiquetado de Envases - Módulo Manifold. Aplica a partir de la fecha de llenado indicada en este documento. El estatus del registro corresponde a \"Aprobado Producción\".";
  const lines = doc.splitTextToSize(disclaimer, contentW - 8);
  doc.text(lines, pageW / 2, y, { align: "center", lineHeightFactor: 1.25 });

  // Footer
  const footerH = 18;
  const footerY = pageH - footerH - 4;
  doc.addImage(footer, "PNG", marginX - 2, footerY, contentW + 4, footerH);

  doc.save(`Expediente_${folio}.pdf`);
};
